import { getLanguages, getCurrentEnvironment } from "./constants";
import { useContext, useState, useEffect } from "react";
import { LanguageContext } from "context/languageContext"; 
import i18next from "i18next";
import styles from "./styles.module.scss"; 

const LanguageDropdown = () => {
  const { language, setLanguage } = useContext(LanguageContext);
  const [languageDropdownIsOpen, setLanguageDropdownIsOpen] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [activeLanguage, setActiveLanguage] = useState(null);
  const [currentEnv, setCurrentEnv] = useState('');
  
  // Initialize languages and detect current language on mount
  useEffect(() => {
    const initializeLanguages = () => {
      const env = getCurrentEnvironment();
      setCurrentEnv(env);
      
      const languagesList = getLanguages();
      setLanguages(languagesList);
      
      // Detect current language based on domain
      const currentHost = window.location.hostname;
      const currentUrl = window.location.href;
      
      // Find current language by checking which URL matches
      let currentLang = null;
      
      // Try to find by exact domain match
      currentLang = languagesList.find(lang => {
        const langUrl = new URL(lang.url);
        const langHostname = langUrl.hostname;
        
        // Check if current hostname matches language hostname
        // This handles www vs non-www and staging vs production
        return currentHost === langHostname || 
               currentHost.replace('www.', '') === langHostname.replace('www.', '');
      });
      
      // If still not found, try by country code in hostname
      if (!currentLang) {
        const countryCodes = ['fr', 'es', 'be', 'uk', 'com'];
        for (const code of countryCodes) {
          if (currentHost.includes(`.${code}`)) {
            if (code === 'com') {
              currentLang = languagesList.find(lang => lang.name === 'en');
            } else {
              currentLang = languagesList.find(lang => lang.name === code);
            }
            if (currentLang) break;
          }
        }
      }
      
      if (currentLang) {
        setLanguage(currentLang.name);
        i18next.changeLanguage(currentLang.value);
        setActiveLanguage(currentLang);
      } else {
        // Fallback to English
        const defaultLang = languagesList.find(lang => lang.name === 'en') || languagesList[0];
        setActiveLanguage(defaultLang);
        setLanguage(defaultLang.name);
        i18next.changeLanguage(defaultLang.value);
      }
    };
    
    if (typeof window !== 'undefined') {
      initializeLanguages();
    }
  }, [setLanguage]);
  
  const handleLanguageClick = (lang) => {
    // Simply redirect to the language-specific URL
    // This will maintain the same environment (staging, new., or www)
    window.location.href = lang.url;
  };

  if (!activeLanguage || languages.length === 0) {
    return null; // or a loading spinner
  }

  const languagesRenderer = languages.map((lang) => (
    <div
      className={`${styles.menuItem} ${
        lang.name.toLowerCase() === activeLanguage.name.toLowerCase() 
          ? styles.active 
          : ""
      }`} 
      key={`${lang.name}-${currentEnv}`} // Include env in key to force re-render on env change
      onClick={() => handleLanguageClick(lang)}
    >
      <img src={lang.icon} alt={`${lang.name} flag`} />
      <span>{lang.name}</span>
      {/* Optional: Show environment indicator */}
      {/* <span className={styles.envBadge}>{currentEnv}</span> */}
    </div>
  ));

  return (
    <div className={styles.chooseLanguage}>
      <button
        className={styles.chooseLanguageButton}
        onClick={() => setLanguageDropdownIsOpen(!languageDropdownIsOpen)}
      >
        <img src={activeLanguage.icon} alt="flag" />
        <span>{activeLanguage.name}</span>
        {/* Optional: Show current environment */}
        {/* <span className={styles.envIndicator}>({currentEnv})</span> */}
        <span className="caret"></span>
      </button>

      <div
        className={`${styles.chooseLanguageMenu} ${
          languageDropdownIsOpen ? styles.visible : styles.hidden
        }`}
      >
        {languagesRenderer}
      </div>
    </div>
  );
};

export default LanguageDropdown;